import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Card,
} from "@mui/material";
import { DateTime } from "luxon";
import { ReactElement, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import {
  ScheduleCampaignFullInitialSendMutation,
  ScheduleCampaignFullInitialSendMutationVariables,
  ScheduleCampaignPartialInitialSendMutation,
  ScheduleCampaignPartialInitialSendMutationVariables,
  StartCampaignFullInitialSendMutation,
  StartCampaignFullInitialSendMutationVariables,
  StartCampaignPartialInitialSendMutation,
  StartCampaignPartialInitialSendMutationVariables,
} from "~frontend/__generated__/graphql";
import {
  RhfDateTimePicker,
  RhfSelect,
} from "~frontend/components/forms/react-hook-form-components";
import { RhfNumberField } from "~frontend/components/forms/react-hook-form-components/RhfNumberField";
import {
  GET_CAMPAIGN_FOR_VIEW,
  SCHEDULE_CAMPAIGN_FULL_INITIAL_SEND,
  SCHEDULE_CAMPAIGN_PARTIAL_INITIAL_SEND,
  START_CAMPAIGN_FULL_INITIAL_SEND,
  START_CAMPAIGN_PARTIAL_INITIAL_SEND,
} from "~frontend/graphql/campaigns";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import { SendSummaryCard } from "~frontend/routes/subaccounts/campaignView/initialSend/SendSummaryCard";
import { useTextingHours } from "~frontend/util/textingHours";
import useMutation from "~frontend/util/useMutation";

import { CampaignForView } from "../types";

function createPickAWinnerSchema(
  isInTextingHours: boolean,
  textingHoursStart: string,
  textingHoursEnd: string,
  textingHoursTimezone: string
) {
  return z
    .object({
      sendingOption: z.string().min(1, "Please select an option"),
      qty: z.coerce.number().int().min(1, "Must be at least 1").optional(),
      scheduleFor: z.coerce
        .date()
        .nullish()
        .refine(d => !d || d > new Date(), {
          message: "Must be in the future",
        }),
    })
    .superRefine((data, ctx) => {
      if (data.sendingOption.endsWith(":partial") && !data.qty) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["qty"],
          message: "Must be at least 1",
        });
      }
      if (!isInTextingHours && !data.scheduleFor) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["scheduleFor"],
          message: "A send date is required when outside of texting hours",
        });
      }
      if (data.scheduleFor) {
        const scheduledTime = DateTime.fromJSDate(data.scheduleFor).setZone(
          textingHoursTimezone
        );
        const timeStr = scheduledTime.toFormat("HH:mm:ss");
        if (timeStr < textingHoursStart || timeStr > textingHoursEnd) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["scheduleFor"],
            message: `Send time must be within texting hours`,
          });
        }
      }
    });
}

type PickAWinnerFormOutput = z.infer<
  ReturnType<typeof createPickAWinnerSchema>
>;

export function PickAWinnerDialog({
  campaign,
  handleClose,
}: {
  campaign: CampaignForView;
  handleClose: () => void;
}): ReactElement {
  const subaccount = useSubaccountAdminConfig()!;
  const variants = campaign.scriptVariants.filter(v => !v.deletedAt);

  const textingHours = {
    start: campaign.textingHoursStart ?? subaccount.textingHoursStart,
    end: campaign.textingHoursEnd ?? subaccount.textingHoursEnd,
    timezone: campaign.textingHoursTimezone ?? subaccount.textingHoursTimezone,
  };
  const { isInTextingHours } = useTextingHours(textingHours);
  const schema = useMemo(
    () =>
      createPickAWinnerSchema(
        isInTextingHours,
        textingHours.start,
        textingHours.end,
        textingHours.timezone
      ),
    [
      isInTextingHours,
      textingHours.start,
      textingHours.end,
      textingHours.timezone,
    ]
  );
  const [sendFull] = useMutation<
    StartCampaignFullInitialSendMutation,
    StartCampaignFullInitialSendMutationVariables
  >(START_CAMPAIGN_FULL_INITIAL_SEND, {
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to start send",
  });

  const [sendPartial] = useMutation<
    StartCampaignPartialInitialSendMutation,
    StartCampaignPartialInitialSendMutationVariables
  >(START_CAMPAIGN_PARTIAL_INITIAL_SEND, {
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to start partial send",
  });

  const [scheduleFull] = useMutation<
    ScheduleCampaignFullInitialSendMutation,
    ScheduleCampaignFullInitialSendMutationVariables
  >(SCHEDULE_CAMPAIGN_FULL_INITIAL_SEND, {
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to schedule send",
  });

  const [schedulePartial] = useMutation<
    ScheduleCampaignPartialInitialSendMutation,
    ScheduleCampaignPartialInitialSendMutationVariables
  >(SCHEDULE_CAMPAIGN_PARTIAL_INITIAL_SEND, {
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to schedule partial send",
  });

  const { control, handleSubmit } = useForm({
    resolver: zodResolver(schema),
    reValidateMode: "onBlur",
  });

  const sendingOption = useWatch({ control, name: "sendingOption" });
  const isPartial = sendingOption?.endsWith(":partial") ?? false;

  async function onSubmit(input: PickAWinnerFormOutput) {
    const [variantId, sendType] = input.sendingOption.split(":");

    if (input.scheduleFor) {
      if (sendType === "partial" && input.qty !== undefined) {
        await schedulePartial({
          variables: {
            input: {
              campaignId: campaign.id,
              qty: input.qty,
              scheduleFor: input.scheduleFor,
              sendWithVariantId: variantId,
            },
          },
        });
      } else {
        await scheduleFull({
          variables: {
            input: {
              campaignId: campaign.id,
              scheduleFor: input.scheduleFor,
              sendWithVariantId: variantId,
            },
          },
        });
      }
    } else {
      if (sendType === "partial" && input.qty !== undefined) {
        await sendPartial({
          variables: {
            input: {
              campaignId: campaign.id,
              qty: input.qty,
              sendWithVariantId: variantId,
            },
          },
        });
      } else {
        await sendFull({
          variables: {
            input: {
              campaignId: campaign.id,
              sendWithVariantId: variantId,
            },
          },
        });
      }
    }

    handleClose();
  }

  const now = DateTime.now();

  return (
    <Dialog open={true} onClose={handleClose} maxWidth="sm">
      <DialogTitle>
        Pick a Winner and Continue Sending {campaign.name}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <RhfDateTimePicker
            name="scheduleFor"
            dateLabel="Send Date"
            timeLabel="Send Time"
            minTime={now}
            minDate={now}
            control={control}
            optional={isInTextingHours}
          />
          <Card sx={{ px: 2, py: 1, mb: 2 }}>
            <RhfSelect
              name="sendingOption"
              label="Sending Option"
              control={control}
              variant="underlined"
              fullWidth
              sx={{ mb: 2, pt: 1 }}
            >
              {variants.flatMap(variant => {
                const displayOrder = (variant.order ?? 0) + 1;
                return [
                  <MenuItem
                    key={`${variant.id}:full`}
                    value={`${variant.id}:full`}
                  >
                    Send variant {displayOrder} to the full list
                  </MenuItem>,
                  <MenuItem
                    key={`${variant.id}:partial`}
                    value={`${variant.id}:partial`}
                  >
                    Send variant {displayOrder} to a portion of the list, then
                    pause
                  </MenuItem>,
                ];
              })}
            </RhfSelect>
            {isPartial && (
              <RhfNumberField
                control={control}
                name="qty"
                fullWidth
                label="Texts to Send"
                sx={{ mb: 2 }}
                variant="standard"
              />
            )}
          </Card>
          <SendSummaryCard campaign={campaign} />

          <DialogActions
            sx={{ justifyContent: "flex-end", pl: 0, pr: 0, pt: 2 }}
          >
            <Button variant="outlined" onClick={handleClose}>
              CLOSE
            </Button>
            <Button variant="contained" type="submit">
              SEND
            </Button>
          </DialogActions>
        </DialogContent>
      </form>
    </Dialog>
  );
}
